import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { SECTORS, useSector } from '@/lib/sector';
import { Icon } from '@/components/icons/Icon';

/**
 * The way across: one 44px control, the size of the cart icon beside it.
 *
 * ── WHY IT SHRANK TO AN ICON ────────────────────────────────────────────────
 *
 * This started as a full-width bar holding two labelled segments. It worked,
 * and it was the largest thing on the home screen — louder than the search box
 * and louder than the shelf, for a control most people use once and then not
 * again for a week. On a 393px phone that is a whole band of vertical space
 * spent on navigation rather than on what the buyer came for.
 *
 * So it is now a switch in the top bar, centred between the wordmark and the
 * cart. It rests on the left for the pharmacy, slides right for the veterinary
 * shop, and slides back when tapped again — the shape everybody already reads
 * as "one of two, and this is the one".
 *
 * ── WHAT CARRIES THE MEANING NOW THE WORDS ARE GONE ─────────────────────────
 *
 * The knob carries the mark of the shop you are IN, not the one you would go
 * to — because on an app selling medicine "which shelf am I looking at" is the
 * question that has to be answered correctly, and a control showing somewhere
 * else is an invitation to misread it. Where it SITS says the same thing again:
 * left is the pharmacy, right is the veterinary shop, always. What tapping does
 * is in the label, which a screen reader announces in full.
 *
 * And it is never the only signal. On the veterinary side the wordmark right
 * beside it reads "ChiCare Vet", a strip across the top of the page says
 * nothing there is for people, the shelf heading says Veterinary, and every
 * card carries its species. Somebody who cannot tell a capsule from a paw at
 * 13px is still in no doubt which shop they are standing in — which is the
 * property that matters, and the reason this control can afford to be small.
 */
export function SectorToggle() {
  const sector = useSector();
  const here = SECTORS[sector];
  const other = SECTORS[sector === 'veterinary' ? 'human' : 'veterinary'];
  const isVet = sector === 'veterinary';

  return (
    <Link
      to={other.home}
      data-testid="sector-toggle"
      data-sector-here={sector}
      // `switch` rather than `link`, because that is what it now looks like and
      // a control that looks like a switch and announces itself as a link is a
      // control nobody can predict. It is still an anchor underneath — the URL
      // is the sector, so this stays shareable and Back still works.
      role="switch"
      aria-checked={isVet}
      // Both halves said out loud: where you are, and what this does. An
      // unlabelled switch leaves a screen reader user guessing at exactly the
      // thing the sighted page states four times over.
      aria-label={`${here.label} — switch to ${other.label}`}
      title={`Switch to ${other.label}`}
      // The tappable box is 44px tall (§11) while the switch drawn inside it is
      // 28px. Shrinking the target to match the graphic is how a control this
      // small becomes one people miss twice before hitting.
      className="grid h-11 w-[50px] shrink-0 place-items-center no-underline"
    >
      <span
        aria-hidden
        className={cn(
          'relative block h-7 w-[50px] rounded-pill border transition-colors',
          isVet ? 'border-blue bg-blue-tint' : 'border-line bg-paper',
        )}
      >
        {/* The knob. Blue on both sides and carrying the current shop's mark,
            so the thing that moved is the thing you are looking at. Moved with
            a transform rather than by changing `left`, so it costs a composite
            and not a layout — on the cheap Android this is built for, that is
            the difference between a slide and a stutter. */}
        <span
          className="absolute left-[2px] top-[2px] grid h-6 w-6 place-items-center rounded-full bg-blue text-white shadow-sm transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${isVet ? 22 : 0}px)` }}
        >
          <Icon name={here.icon} size={13} strokeWidth={2.1} />
        </span>
      </span>
    </Link>
  );
}

/**
 * The same crossing, said in a sentence, for the desktop header.
 *
 * At 1280px and up there is room for the words, and the header already lists
 * this side's categories — so what is missing there is the other shop, and a
 * grey utility link among Help and Track order does not read as half the
 * business. This does.
 */
export function SectorCrossing({ className }: { className?: string }) {
  const sector = useSector();
  const other = SECTORS[sector === 'veterinary' ? 'human' : 'veterinary'];

  return (
    <Link
      to={other.home}
      data-testid="sector-crossing"
      className={cn(
        'inline-flex min-h-9 items-center gap-1.5 rounded-pill border border-blue bg-blue-tint px-3.5 text-[12.5px] font-bold text-blue no-underline hover:bg-blue hover:text-white',
        className,
      )}
    >
      <Icon name={other.icon} size={13} strokeWidth={2} />
      {other.label}
    </Link>
  );
}
